import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { fetchAllPages, wantsAllPages } from "./fetchAllPages";
import type { DeviceModel, DeviceModelFilesystem, DeviceType, ListResponse, SingleResponse } from "./types";

interface ListParams {
  limit?: number;
  cursor?: string;
}

interface DeviceModelListParams extends ListParams {
  deviceTypeId?: string;
  enabled?: boolean;
}

function buildQuery(params: ListParams & { deviceTypeId?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.deviceTypeId) qs.set("deviceTypeId", params.deviceTypeId);
  return qs.toString();
}

// Device types and models are small catalogues used as lookups (labels,
// config schemas) and pickers. Called without limit/cursor, these hooks
// load every page so an entry past the first page is never "Unknown".
function listCatalogue<T>(path: string, params: ListParams & { deviceTypeId?: string }) {
  const query = buildQuery(params);
  const url = `${path}${query ? `?${query}` : ""}`;
  return wantsAllPages(params) ? fetchAllPages<T>(url) : apiFetch<ListResponse<T>>(url);
}

export function useDeviceTypes(params: ListParams = {}) {
  return useQuery({
    queryKey: ["device-types", params],
    queryFn: () => listCatalogue<DeviceType>("/api/v1/device-types", params),
    placeholderData: keepPreviousData,
  });
}

export function useDeviceModels(params: DeviceModelListParams = {}) {
  return useQuery({
    queryKey: ["device-models", params],
    queryFn: () => listCatalogue<DeviceModel>("/api/v1/device-models", params),
    enabled: params.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useAdminDeviceTypes(params: ListParams = {}) {
  return useQuery({
    queryKey: ["admin-device-types", params],
    queryFn: () => listCatalogue<DeviceType>("/api/v1/admin/device-types", params),
    placeholderData: keepPreviousData,
  });
}

export function useAdminDeviceModels(params: DeviceModelListParams = {}) {
  return useQuery({
    queryKey: ["admin-device-models", params],
    queryFn: () => listCatalogue<DeviceModel>("/api/v1/admin/device-models", params),
    enabled: params.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export interface CreateDeviceTypeRequest {
  code: string;
  name: string;
  description?: string;
  status?: "active" | "deprecated" | "draft";
  fields: DeviceType["fields"];
  defaultPayload?: Record<string, unknown>;
}

export interface UpdateDeviceTypeRequest {
  name: string;
  description?: string;
  status?: "active" | "deprecated" | "draft";
  fields: DeviceType["fields"];
  defaultPayload?: Record<string, unknown>;
}

export interface UpsertDeviceModelRequest {
  modelCode?: string;
  deviceTypeId: string;
  name: string;
  description?: string;
  status?: "active" | "deprecated" | "draft";
  // Omitted -> the API's defaults (littlefs, 1.5 MiB).
  filesystem?: DeviceModelFilesystem;
  resourceBudgetBytes?: number;
}

export function useCreateDeviceType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDeviceTypeRequest) =>
      apiFetch<SingleResponse<DeviceType>>("/api/v1/admin/device-types", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-device-types"] });
      qc.invalidateQueries({ queryKey: ["device-types"] });
    },
  });
}

export function useUpdateDeviceType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceTypeId, body }: { deviceTypeId: string; body: UpdateDeviceTypeRequest }) =>
      apiFetch<SingleResponse<DeviceType>>(`/api/v1/admin/device-types/${deviceTypeId}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["admin-device-types"] });
      qc.invalidateQueries({ queryKey: ["device-types"] });
      qc.invalidateQueries({ queryKey: ["admin-device-type", vars.deviceTypeId] });
    },
  });
}

export function useCreateDeviceModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertDeviceModelRequest) =>
      apiFetch<SingleResponse<DeviceModel>>("/api/v1/admin/device-models", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-device-models"] });
      qc.invalidateQueries({ queryKey: ["device-models"] });
    },
  });
}
